import React from 'react';
import { CheckCircle, XCircle, AlertCircle, Loader2 } from 'lucide-react';
import { HealthStatus } from '../types/health';
import { AuthState } from '../contexts/healthStatusContextDef';

interface InitializationLoaderProps {
  isInitializing: boolean;
  healthStatus: HealthStatus | null;
  error: string | null;
  initializationMessage: string;
  authState: AuthState;
}

const FORCE_REFRESH_TIMEOUT_MS = 60_000; // 1 minute

export const InitializationLoader: React.FC<InitializationLoaderProps> = ({
  isInitializing,
  healthStatus,
  error,
  initializationMessage,
  authState,
}) => {
  React.useEffect(() => {
    if (!isInitializing) return;

    const timer = setTimeout(() => {
      window.location.reload();
    }, FORCE_REFRESH_TIMEOUT_MS);

    return () => clearTimeout(timer);
  }, [isInitializing]);

  if (!isInitializing) {
    return null;
  }

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'healthy':
        return <CheckCircle className="w-6 h-6 text-green-600" />;
      case 'unhealthy':
        return <XCircle className="w-6 h-6 text-red-600" />;
      case 'degraded':
        return <AlertCircle className="w-6 h-6 text-yellow-600" />;
      default:
        return <Loader2 className="w-6 h-6 text-blue-600 animate-spin" />;
    }
  };

  const getComponentDisplayName = (name: string): string => {
    const displayNames: Record<string, string> = {
      'elasticsearch': 'Elasticsearch',
      'ollama': 'Ollama (Embedding Provider)',
      'database': 'Database',
      'cache': 'Cache',
    };
    return displayNames[name.toLowerCase()] || name;
  };

  return (
    <div 
      data-component="InitializationLoader" 
      className="fixed inset-0 bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center z-[100]"
    >
      <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full mx-4 p-8">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full mb-4">
            <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            System Initialization
          </h1>
          <p className="text-sm text-gray-600">
            {initializationMessage}
          </p>
        </div>

        {/* Error Display */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex items-start gap-3">
              <XCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-red-900">Connection Error</p>
                <p className="text-sm text-red-700 mt-1">{error}</p>
              </div>
            </div>
          </div>
        )}

        {/* Authentication Status */}
        <div className="space-y-3 mb-3">
          <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg transition-all hover:bg-gray-100">
            <div className="flex-shrink-0">
              {authState === 'authenticated' ? (
                <CheckCircle className="w-6 h-6 text-green-600" />
              ) : authState === 'redirecting' ? (
                <AlertCircle className="w-6 h-6 text-yellow-600" />
              ) : (
                <Loader2 className="w-6 h-6 text-blue-600 animate-spin" />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-900">Authentication</p>
              {authState === 'redirecting' && (
                <p className="text-xs text-gray-600 mt-1">Redirecting to login...</p>
              )}
            </div>
            <div className="flex-shrink-0">
              <span
                className={`text-xs font-medium px-2 py-1 rounded-full ${
                  authState === 'authenticated'
                    ? 'bg-green-100 text-green-700'
                    : authState === 'redirecting'
                    ? 'bg-yellow-100 text-yellow-700'
                    : 'bg-gray-100 text-gray-700'
                }`}
              >
                {authState === 'authenticated' ? 'authenticated' : authState === 'redirecting' ? 'redirecting' : 'checking'}
              </span>
            </div>
          </div>
        </div>

        {/* Component Status List */}
        {healthStatus && healthStatus.components && (
          <div className="space-y-3">
            {Object.entries(healthStatus.components).map(([name, component]) => (
              <div
                key={name}
                className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg transition-all hover:bg-gray-100"
              >
                <div className="flex-shrink-0">
                  {getStatusIcon(component.status)}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900">
                    {getComponentDisplayName(name)}
                  </p>
                  {component.message && (
                    <p className="text-xs text-gray-600 mt-1 truncate">
                      {component.message}
                    </p>
                  )}
                </div>
                <div className="flex-shrink-0">
                  <span
                    className={`text-xs font-medium px-2 py-1 rounded-full ${
                      component.status === 'healthy'
                        ? 'bg-green-100 text-green-700'
                        : component.status === 'unhealthy'
                        ? 'bg-red-100 text-red-700'
                        : component.status === 'degraded'
                        ? 'bg-yellow-100 text-yellow-700'
                        : 'bg-gray-100 text-gray-700'
                    }`}
                  >
                    {component.status}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Loading State (when no health status yet) */}
        {!healthStatus && !error && (
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <div
                key={i}
                className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg animate-pulse"
              >
                <div className="w-6 h-6 bg-gray-300 rounded-full"></div>
                <div className="flex-1">
                  <div className="h-4 bg-gray-300 rounded w-3/4"></div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Footer */}
        <div className="mt-8 pt-6 border-t border-gray-200">
          <p className="text-xs text-gray-500 text-center">
            Please wait while the system components are starting up...
          </p>
        </div>
      </div>
    </div>
  );
};
